import { useCallback } from 'react';

import { type PublicClip } from '@air/api';

import { useCopyToWorkspace } from '~/components/CopyToWorkspace/hooks/useCopyToWorkspace';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { type GetItemMenuOptionsCallback } from '~/constants/MenuOptions';
import { useGetPublicCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { useGetPublicClipMenuOptions } from '~/hooks/useGetPublicClipMenuOptions';
import { useGoToPublicAssetPage } from '~/hooks/useGoToPublicAssetPage';
import { canExportAsset, canSeeAssetVersions } from '~/utils/permissions/assetPermissions';

export const useGetPublicAssetMenuOptionsCallback = () => {
  const { copyToAir } = useCopyToWorkspace();
  const { goToAssetPage } = useGoToPublicAssetPage();
  const { permissions } = usePublicPermissions();

  const { getCustomFieldsMenuOptions } = useGetPublicCustomFieldsMenuOptions();
  const { getClipMenuOptions } = useGetPublicClipMenuOptions();

  const getAssetMenuOptionsCallback: GetItemMenuOptionsCallback<PublicClip> = useCallback(
    ({ item: clip }) =>
      ({ trackLocation }) => {
        const canViewAssetVersions = canSeeAssetVersions(permissions);

        const canCopyToWorkspace = canExportAsset(permissions);

        return getClipMenuOptions({
          customFieldsMenuOptions: getCustomFieldsMenuOptions({ assets: [clip] }),
          onCopyToAir: canCopyToWorkspace
            ? () => {
                /**
                 * We only allow the entire asset to be copied if canViewAssetVerions
                 * is true. Else, we just let them copy that single clip (asset version)
                 */
                if (canViewAssetVersions) {
                  copyToAir({
                    assetId: clip.assetId,
                    trackLocation,
                  });
                } else {
                  copyToAir({
                    clipId: clip.id,
                    trackLocation,
                  });
                }
              }
            : undefined,

          onOpen: () => goToAssetPage({ asset: clip, trackLocation: 'open-dropdown-select' }),
        });
      },
    [permissions, getClipMenuOptions, getCustomFieldsMenuOptions, copyToAir, goToAssetPage],
  );

  return {
    getAssetMenuOptionsCallback,
  };
};
